// Pure rules for the "Add an ad to a live ad set" media choice (Part A, true duplicate).
// No I/O. Shared by the composer, the validator, the server update lock and the mapper.
//
//   new      upload a new image or video (the original flow)
//   original reuse the source ad's image hash / video id; the copy stays editable
//   exact    attach the source creative unchanged (existingCreativeId); nothing is editable
//
// ⚠️ Meta thumbnails are NEVER re-used as creative by URL. "Original" sends the hash or the
// video id, which Meta resolves inside the same ad account.

import type { AttachMediaMode, AttachTarget } from '../types/metaAdDraft.js';

export const ATTACH_MEDIA_MODES: readonly AttachMediaMode[] = ['new', 'original', 'exact'];

export function isAttachMediaMode(v: unknown): v is AttachMediaMode {
  return typeof v === 'string' && (ATTACH_MEDIA_MODES as readonly string[]).includes(v);
}

function has(v: string | null | undefined): boolean {
  return typeof v === 'string' && v.trim() !== '';
}

export function mediaModeOf(target: { mediaMode?: unknown } | null | undefined): AttachMediaMode {
  return isAttachMediaMode(target?.mediaMode) ? (target!.mediaMode as AttachMediaMode) : 'new';
}

/** What the source ad lets the AM reuse. */
export function reusableMedia(
  t: Pick<AttachTarget, 'sourceImageHash' | 'sourceVideoId' | 'sourceCreativeId'>,
): { original: 'image' | 'video' | null; exact: boolean } {
  return {
    original: has(t.sourceVideoId) ? 'video' : has(t.sourceImageHash) ? 'image' : null,
    exact: has(t.sourceCreativeId),
  };
}

export function modeIsAvailable(
  mode: AttachMediaMode,
  t: Pick<AttachTarget, 'sourceImageHash' | 'sourceVideoId' | 'sourceCreativeId'>,
): boolean {
  if (mode === 'new') return true;
  const r = reusableMedia(t);
  return mode === 'original' ? r.original !== null : r.exact;
}

/**
 * The attach target stored on update: EVERYTHING comes from the stored draft except the
 * media mode, which may change to a mode the stored source actually supports. A client
 * cannot change the ad set, the source ids or the identity through an update.
 */
export function mergeAttachTargetOnUpdate(
  before: AttachTarget | null | undefined,
  incoming: { mediaMode?: unknown } | null | undefined,
): AttachTarget | null {
  if (!before) return null;
  const wanted = incoming?.mediaMode;
  const mediaMode = isAttachMediaMode(wanted) && modeIsAvailable(wanted, before) ? wanted : mediaModeOf(before);
  // Legacy drafts carry no mediaMode and keep carrying none until one is actually chosen.
  return mediaMode === 'new' && !('mediaMode' in before) ? { ...before } : { ...before, mediaMode };
}

/** Meta's "http://fb.me/" is a placeholder on lead ads, never a real website link. */
export function isFbMePlaceholder(url: string | null | undefined): boolean {
  const u = (url ?? '').trim();
  if (u === '') return false;
  try {
    const host = new URL(/^[a-z]+:\/\//i.test(u) ? u : `http://${u}`).hostname.toLowerCase();
    return host === 'fb.me' || host === 'www.fb.me';
  } catch {
    return /^(https?:\/\/)?(www\.)?fb\.me(\/|$)/i.test(u);
  }
}
